import { useEffect, useState } from "react";
import { Trophy, BarChart3, MessageSquareText, EyeOff } from "lucide-react";
import { useBrandsStore } from "@/stores/brands";
import { usePromptsStore } from "@/stores/prompts";
import { useDefaultModelSeed, useModelsStore } from "@/stores/models";
import { api } from "@/api/client";
import type { StudyType } from "../../../types";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { BrandSelector } from "@/components/compare/BrandSelector";
import { ModelSelector } from "@/components/compare/ModelSelector";
import { LeakWarning } from "@/components/ui/leak-warning";
import { PromptSelect } from "@/components/prompts/PromptSelect";
import { cn } from "@/lib/utils";
import { useBlindQueryLeakCheck } from "@/lib/useBlindQueryLeakCheck";

interface CreateStudyFormProps {
  onSuccess: () => void;
}

// Tournaments are retired: existing tournament studies keep running and their
// history stays readable, but new ones can't be created.
const studyTypes: { id: StudyType; label: string; description: string; icon: typeof BarChart3; minBrands: number }[] = [
  { id: "sentiment", label: "Sentiment", description: "Natural language query analysis", icon: MessageSquareText, minBrands: 1 },
  { id: "scoring", label: "Scoring", description: "Structured dimension evaluation", icon: BarChart3, minBrands: 1 },
  { id: "blind_query", label: "Blind Query", description: "Monitor brand mentions after an unguided answer", icon: EyeOff, minBrands: 0 },
];

export function CreateStudyForm({ onSuccess }: CreateStudyFormProps) {
  const fetchBrands = useBrandsStore(s => s.fetch);
  const fetchPrompts = usePromptsStore(s => s.fetch);
  const fetchModels = useModelsStore(s => s.fetch);
  const prompts = usePromptsStore(s => s.prompts);
  const brands = useBrandsStore(s => s.brands);

  const [name, setName] = useState("");
  const [type, setType] = useState<StudyType>("sentiment");
  const [selectedBrands, setSelectedBrands] = useState<Set<number>>(new Set());
  const [promptId, setPromptId] = useState("");
  const [selectedModels, setSelectedModels] = useState<Set<string>>(new Set());
  const [error, setError] = useState("");

  useEffect(() => { fetchBrands(); fetchPrompts(); fetchModels(); }, []);

  useDefaultModelSeed(true, (ids) => setSelectedModels((prev) => (prev.size > 0 ? prev : new Set(ids))));

  const selectedType = studyTypes.find(t => t.id === type)!;
  const selectedPrompt = prompts.find(p => String(p.id) === promptId);
  const selectedBrandNames = brands.filter(b => selectedBrands.has(b.id)).map(b => b.name);
  const autoTrackAllBrands = type === "blind_query" && selectedBrands.size === 0;
  const leakCheckBrandNames = autoTrackAllBrands ? brands.map(b => b.name) : selectedBrandNames;
  const { brandNamesInPrompt, hasLeak: hasBlindQueryPromptLeak } = useBlindQueryLeakCheck({
    enabled: type === "blind_query",
    promptTemplate: selectedPrompt?.template,
    brandNames: leakCheckBrandNames,
  });
  const brandRequirementLabel = type === "blind_query" ? "Brands (optional)" : `Brands (min ${selectedType.minBrands})`;

  const handleSubmit = async () => {
    if (!name.trim()) { setError("Name is required"); return; }
    if (type === "blind_query" && brands.length === 0) { setError("Add at least one workspace brand to track after each Blind Query response."); return; }
    if (selectedBrands.size < selectedType.minBrands) { setError(`At least ${selectedType.minBrands} brand${selectedType.minBrands === 1 ? "" : "s"} required`); return; }
    if (!promptId) { setError("Select a prompt"); return; }
    if (selectedModels.size < 1) { setError("Select at least 1 model"); return; }
    if (hasBlindQueryPromptLeak) { setError("Remove tracked brand names from the prompt before creating a Blind Query study."); return; }
    setError("");

    try {
      await api.studies.create({
        name: name.trim(),
        type,
        brand_ids: Array.from(selectedBrands),
        prompt_id: parseInt(promptId),
        models: Array.from(selectedModels),
      });
      onSuccess();
    } catch (err: any) {
      setError(err.message);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>New Study</CardTitle>
        <CardDescription>Track brand perception over time with repeated runs</CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div>
          <Label htmlFor="study-name">Study Name</Label>
          <Input
            id="study-name"
            placeholder="e.g. Daily Meso Brand Rankings"
            value={name}
            onChange={e => setName(e.target.value)}
            autoFocus
          />
        </div>

        <div>
          <Label>Type</Label>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mt-0.5">
            {studyTypes.map(t => (
              <button
                key={t.id}
                type="button"
                onClick={() => setType(t.id)}
                className={cn(
                  "flex flex-col items-center gap-1.5 rounded-md border p-3 text-center transition-all duration-150 cursor-pointer",
                  type === t.id
                    ? "border-brand-600 bg-brand-50 ring-1 ring-brand-600/20"
                    : "border-slate-200 bg-white hover:border-slate-300"
                )}
              >
                <t.icon className={cn("w-4 h-4", type === t.id ? "text-brand-600" : "text-slate-400")} />
                <span className={cn("text-[12px] font-semibold", type === t.id ? "text-brand-950" : "text-slate-600")}>{t.label}</span>
                <span className="sr-only">{t.description}</span>
              </button>
            ))}
          </div>
          <p className="mt-2 text-[12px] text-slate-500 leading-relaxed">{selectedType.description}</p>
        </div>

        <div>
          <Label>{brandRequirementLabel}</Label>
          <BrandSelector selected={selectedBrands} onChange={setSelectedBrands} />
          {type === "blind_query" && (
            <p className="mt-3 text-[12px] leading-relaxed text-slate-500">
              {autoTrackAllBrands
                ? "All workspace brands will be tracked after each response. None are sent to the model."
                : "Selected tracked brands are monitored after the response. They are not sent to the model."}
            </p>
          )}
        </div>

        <div>
          <Label htmlFor="study-prompt">Prompt</Label>
          <PromptSelect
            id="study-prompt"
            value={promptId}
            onChange={e => setPromptId(e.target.value)}
            prompts={prompts}
            placeholder="Select..."
          />
        </div>

        <div>
          <Label>Models (min 1)</Label>
          <ModelSelector selected={selectedModels} onChange={setSelectedModels} />
        </div>

        {type === "blind_query" && (
          <LeakWarning brandNames={brandNamesInPrompt} context="blind query study" />
        )}

        {error && (
          <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-[13px] text-red-700">{error}</p>
          </div>
        )}

        <Button onClick={handleSubmit} disabled={hasBlindQueryPromptLeak} title={hasBlindQueryPromptLeak ? "Remove tracked brand names from the prompt before creating a Blind Query study." : undefined}>Create Study</Button>
      </CardContent>
    </Card>
  );
}
